import { initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, GoogleAuthProvider, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, type Functions } from 'firebase/functions';
import type { RuntimeImportMeta } from '../utils/runtimeEnv';

type FirebaseConfigShape = {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
};

const nodeEnv = ((globalThis as unknown as { process?: { env?: Record<string, string | undefined> } }).process?.env) || {};
const viteEnv = ((import.meta as unknown as RuntimeImportMeta).env || {}) as Record<string, string | undefined>;
const envValue = (key: string) => viteEnv[key] || nodeEnv[key];

const primaryApiKey = envValue('VITE_FIREBASE_API_KEY');
const fallbackApiKey = envValue('VITE_FIREBASE_API_KEY_FALLBACK');
const effectiveApiKey = primaryApiKey || fallbackApiKey;

const firebaseConfig: FirebaseConfigShape = {
  apiKey: effectiveApiKey,
  authDomain: envValue('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: envValue('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: envValue('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: envValue('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: envValue('VITE_FIREBASE_APP_ID')
};

const localConfigModules = typeof (import.meta as unknown as { glob?: unknown }).glob === 'function'
  ? (import.meta as unknown as { glob: <T>(pattern: string, options: { eager: boolean }) => Record<string, T> }).glob<{ localFirebaseConfig: FirebaseConfigShape }>('./localFirebaseConfig.ts', {
    eager: true
  })
  : {};
const localConfig = envValue('VITE_USE_LOCAL_FIREBASE_CONFIG') === 'true'
  ? localConfigModules['./localFirebaseConfig.ts']?.localFirebaseConfig
  : undefined;

const activeFirebaseConfig = localConfig || firebaseConfig;
export const firebaseClientConfig = activeFirebaseConfig;

const envNameByKey = {
  apiKey: 'VITE_FIREBASE_API_KEY',
  authDomain: 'VITE_FIREBASE_AUTH_DOMAIN',
  projectId: 'VITE_FIREBASE_PROJECT_ID',
  storageBucket: 'VITE_FIREBASE_STORAGE_BUCKET',
  messagingSenderId: 'VITE_FIREBASE_MESSAGING_SENDER_ID',
  appId: 'VITE_FIREBASE_APP_ID'
} as const;

const requiredKeys = Object.keys(envNameByKey) as Array<keyof typeof envNameByKey>;

const mask = (value?: string) => {
  if (!value) return '(missing)';
  if (value.length <= 10) return `${value.slice(0, 2)}...`;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

const isBlankOrPlaceholder = (value?: string) => !value || value.includes('replace-me');
const missingKeys = requiredKeys.filter((key) => isBlankOrPlaceholder(activeFirebaseConfig[key]));
const warnings: string[] = [];

if (localConfig && envValue('VITE_USE_LOCAL_FIREBASE_CONFIG') === 'true') {
  warnings.push('Using localFirebaseConfig.ts because VITE_USE_LOCAL_FIREBASE_CONFIG=true.');
}

if (activeFirebaseConfig.projectId && activeFirebaseConfig.projectId !== 'sg-pfs') {
  warnings.push(`Expected projectId "sg-pfs" but got "${activeFirebaseConfig.projectId}".`);
}

if (activeFirebaseConfig.authDomain && activeFirebaseConfig.projectId && !activeFirebaseConfig.authDomain.includes(activeFirebaseConfig.projectId)) {
  warnings.push('authDomain does not appear to match projectId.');
}

if (activeFirebaseConfig.storageBucket && activeFirebaseConfig.projectId && !activeFirebaseConfig.storageBucket.includes(activeFirebaseConfig.projectId)) {
  warnings.push('storageBucket does not appear to match projectId.');
}

export const firebaseConfigPreview = {
  apiKey: activeFirebaseConfig.apiKey ? 'present' : 'missing',
  apiKeyPrefix: activeFirebaseConfig.apiKey ? activeFirebaseConfig.apiKey.slice(0, 6) : '(missing)',
  apiKeySource: localConfig ? 'localFirebaseConfig.ts' : primaryApiKey ? 'VITE_FIREBASE_API_KEY' : fallbackApiKey ? 'VITE_FIREBASE_API_KEY_FALLBACK' : '(missing)',
  authDomain: activeFirebaseConfig.authDomain || '(missing)',
  projectId: activeFirebaseConfig.projectId || '(missing)',
  storageBucket: activeFirebaseConfig.storageBucket || '(missing)',
  messagingSenderId: activeFirebaseConfig.messagingSenderId ? 'present' : 'missing',
  messagingSenderIdPrefix: mask(activeFirebaseConfig.messagingSenderId),
  appId: activeFirebaseConfig.appId ? 'present' : 'missing',
  appIdPrefix: mask(activeFirebaseConfig.appId)
};

export let firebaseConfigError = missingKeys.length
  ? `Missing Firebase env values: ${missingKeys.map((key) => envNameByKey[key]).join(', ')}. Create .env in the project root and restart Vite.`
  : '';

if (warnings.length) console.warn('[PFS Firebase config warning]', warnings);
console.info('[PFS Firebase config]', firebaseConfigPreview);

let appInstance: FirebaseApp | undefined;
let authInstance: Auth | undefined;
let dbInstance: Firestore | undefined;
let storageInstance: FirebaseStorage | undefined;
let functionsInstance: Functions | undefined;

if (!firebaseConfigError) {
  try {
    appInstance = initializeApp(activeFirebaseConfig);
    authInstance = getAuth(appInstance);
    dbInstance = getFirestore(appInstance);
    storageInstance = getStorage(appInstance);
    functionsInstance = getFunctions(appInstance);
    const firestoreHost = envValue('VITE_FIRESTORE_EMULATOR_HOST') || envValue('FIRESTORE_EMULATOR_HOST');
    const authHost = envValue('VITE_FIREBASE_AUTH_EMULATOR_HOST') || envValue('FIREBASE_AUTH_EMULATOR_HOST');
    if (firestoreHost && nodeEnv.PFS_EMULATOR_TESTS === '1') {
      const [host, port] = firestoreHost.replace(/^https?:\/\//, '').split(':');
      connectFirestoreEmulator(dbInstance, host, Number(port || 8080));
    }
    if (authHost && nodeEnv.PFS_EMULATOR_TESTS === '1') {
      connectAuthEmulator(authInstance, authHost.startsWith('http') ? authHost : `http://${authHost}`, { disableWarnings: true });
    }
  } catch (error) {
    firebaseConfigError = error instanceof Error ? error.message : String(error);
    console.error('[PFS Firebase init failed]', firebaseConfigError, firebaseConfigPreview);
  }
} else {
  console.error('[PFS Firebase config missing]', firebaseConfigError, firebaseConfigPreview);
}

export const app = appInstance as FirebaseApp;
export const auth = authInstance as Auth;
export const db = dbInstance as Firestore;
export const storage = storageInstance as FirebaseStorage;
export const functions = functionsInstance as Functions;
export const googleProvider = new GoogleAuthProvider();
